import classNames from "classnames"
import { GatsbyImage, StaticImage } from "gatsby-plugin-image"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"

import { useTranslation } from "../../../i18n/translate"
import ArrowLeftIcon from "../../../images/icons/arrow_left.svg"
import ArrowRightIcon from "../../../images/icons/arrow_right.svg"
import { HorizontalScrollContainerHelper } from "../../../util/horizontal-scroll-container-helper"
import InlineYoutubeVideo from "../../inline-youtube-video/inline-youtube-video"
import Tile from "../../tile/tile"
import TileContentSpacer from "../../tile/tile-content-spacer/tile-content-spacer"
import "./studies-and-training-comparison.sass"

const StudiesAndTrainingComparison = ({
  children,
  tileTitleTranslatedString,
  items,
}) => {
  const { t } = useTranslation()
  const comparisonRef = useRef(null)

  const [heights, setHeights] = useState({
    title: 0,
    description: 0,
    thatAwaitsYou: 0,
    yourSkills: 0,
    details: 0,
  })

  // const [containerHelperState, setContainerHelperState] = useState({leftButtonActive: false, rightButtonActive: true})
  const [leftButtonActive, setLeftButtonActive] = useState(false)
  const [rightButtonActive, setRightButtonActive] = useState(true)
  const containerHelper = useRef(null)

  useEffect(() => {
    update()

    setTimeout(() => {
      update()
    }, 100)
    window.addEventListener("resize", update)

    const scrollContainerHelper = new HorizontalScrollContainerHelper(
      comparisonRef.current,
      (state) => {
        // setContainerHelperState(state)
        setLeftButtonActive(state.leftButtonActive)
        setRightButtonActive(state.rightButtonActive)
      },
      300,
    )
    containerHelper.current = scrollContainerHelper
    return () => {
      window.removeEventListener("resize", update)
      scrollContainerHelper.destroy()
    }
  }, [])

  const update = () => {
    if (!comparisonRef) {
      return
    }
    const maxTitleHeight = [
      ...comparisonRef.current.querySelectorAll(
        "._fp-studies-and-training-comparison__column__title",
      ),
    ]
      .map((a) => a.getBoundingClientRect().height)
      .reduce((a, b) => Math.max(a, b), 0)
    const maxDescriptionHeight = [
      ...comparisonRef.current.querySelectorAll(
        "._fp-studies-and-training-comparison__column__description",
      ),
    ]
      .map((a) => a.getBoundingClientRect().height)
      .reduce((a, b) => Math.max(a, b), 0)
    const maxThatAwaitsYouHeight = [
      ...comparisonRef.current.querySelectorAll(
        "._fp-studies-and-training-comparison__column__that-awaits-you",
      ),
    ]
      .map((a) => a.getBoundingClientRect().height)
      .reduce((a, b) => Math.max(a, b), 0)
    const maxYourSkillsHeight = [
      ...comparisonRef.current.querySelectorAll(
        "._fp-studies-and-training-comparison__column__your-skills",
      ),
    ]
      .map((a) => a.getBoundingClientRect().height)
      .reduce((a, b) => Math.max(a, b), 0)
    const maxDetailsHeight = [
      ...comparisonRef.current.querySelectorAll(
        "._fp-studies-and-training-comparison__column__details",
      ),
    ]
      .map((a) => a.getBoundingClientRect().height)
      .reduce((a, b) => Math.max(a, b), 0)

    setHeights({
      title: maxTitleHeight,
      description: maxDescriptionHeight,
      details: maxDetailsHeight,
      thatAwaitsYou: maxThatAwaitsYouHeight,
      yourSkills: maxYourSkillsHeight,
    })
  }

  const iconButtons = [
    {
      iconComponent: () => <ArrowLeftIcon />,
      active: leftButtonActive,
      onClick: () => containerHelper.current.scrollLeft(),
    },
    {
      iconComponent: () => <ArrowRightIcon />,
      active: rightButtonActive,
      onClick: () => containerHelper.current.scrollRight(),
    },
  ]

  console.log(items)

  return (
    <Tile
      titleTranslatedString={tileTitleTranslatedString}
      iconButtons={items.length > 1 ? iconButtons : []}
    >
      <div
        className={classNames(
          "_fp-studies-and-training-comparison",
          `n-${Math.min(items.length, 4)}`,
        )}
        ref={comparisonRef}
      >
        {items.map((item) => {
          return (
            <div className={"_fp-studies-and-training-comparison__column"}>
              <div
                className={"_fp-studies-and-training-comparison__column__image"}
              >
                {item.image && (
                  <GatsbyImage
                    style={{
                      position: "absolute",
                      width: "100%",
                      top: 0,
                      height: "100%",
                      objectFit: "cover",
                    }}
                    image={item.image}
                  />
                )}
              </div>
              <div
                style={{ minHeight: heights.description + "px" }}
                className={
                  "_fp-studies-and-training-comparison__column__field-wrapper"
                }
              >
                <div
                  className={
                    "_fp-studies-and-training-comparison__column__description _fp-text"
                  }
                >
                  {item.description}
                </div>
              </div>
              <div
                style={{ minHeight: heights.title + "px" }}
                className={
                  "_fp-studies-and-training-comparison__column__field-wrapper"
                }
              >
                <div
                  className={
                    "_fp-studies-and-training-comparison__column__title"
                  }
                >
                  <div
                    className={
                      "_fp-studies-and-training-comparison__column__title__title-1 _fp-text"
                    }
                  >
                    {item.title1}
                  </div>
                  <div
                    className={
                      "_fp-studies-and-training-comparison__column__title__title-2 _fp-heading-2"
                    }
                  >
                    {item.title2}
                  </div>
                  <div
                    className={
                      "_fp-text _fp-studies-and-training-comparison__column__title__title-3 "
                    }
                  >
                    {item.title3}
                  </div>
                  <div
                    className={
                      "_fp-studies-and-training-comparison__column__title__title-4 _fp-text"
                    }
                  >
                    {item.title4}
                  </div>
                </div>
              </div>
              <div
                style={{ minHeight: heights.thatAwaitsYou + "px" }}
                className={
                  "_fp-studies-and-training-comparison__column__field-wrapper"
                }
              >
                {item.thatAwaitsYou && (
                  <div className="_fp-studies-and-training-comparison__column__text _fp-text _fp-studies-and-training-comparison__column__that-awaits-you">
                    <div className="_fp-studies-and-training-comparison__column__text__title _fp-heading-5">
                      {t(
                        "CAREER-STUDIES_AND_TRAINING-OVERVIEW-THAT_AWAITS_YOU_COLON",
                      )}
                    </div>
                    {item.thatAwaitsYou}
                  </div>
                )}
              </div>
              <div
                style={{ minHeight: heights.yourSkills + "px" }}
                className={
                  "_fp-studies-and-training-comparison__column__field-wrapper"
                }
              >
                {item.yourSkills && (
                  <div className="_fp-studies-and-training-comparison__column__text _fp-text _fp-studies-and-training-comparison__column__your-skills">
                    <div className="_fp-studies-and-training-comparison__column__text__title _fp-heading-5">
                      {t(
                        "CAREER-STUDIES_AND_TRAINING-OVERVIEW-YOUR_SKILLS_COLON",
                      )}
                    </div>
                    {item.yourSkills}
                  </div>
                )}
              </div>
              <div
                style={{ minHeight: heights.details + "px" }}
                className={
                  "_fp-studies-and-training-comparison__column__field-wrapper"
                }
              >
                {(item.diploma ||
                  item.university ||
                  item.start ||
                  item.duration) && (
                  <div
                    className={
                      "_fp-studies-and-training-comparison__column__details"
                    }
                  >
                    {item.university && (
                      <div
                        className={
                          "_fp-studies-and-training-comparison__column__details__row"
                        }
                      >
                        <div
                          className={
                            "_fp-studies-and-training-comparison__column__details__row__label _fp-heading-5"
                          }
                        >
                          {t("CAREER-STUDIES_AND_TRAINING-OVERVIEW-UNIVERSITY")}
                        </div>
                        {item.university}
                      </div>
                    )}
                    {item.duration && (
                      <div
                        className={
                          "_fp-studies-and-training-comparison__column__details__row"
                        }
                      >
                        <div
                          className={
                            "_fp-studies-and-training-comparison__column__details__row__label _fp-heading-5"
                          }
                        >
                          {t("CAREER-STUDIES_AND_TRAINING-OVERVIEW-DURATION")}
                        </div>
                        {item.duration}
                      </div>
                    )}
                    {item.diploma && (
                      <div
                        className={
                          "_fp-studies-and-training-comparison__column__details__row"
                        }
                      >
                        <div
                          className={
                            "_fp-studies-and-training-comparison__column__details__row__label _fp-heading-5"
                          }
                        >
                          {t("CAREER-STUDIES_AND_TRAINING-OVERVIEW-DIPLOMA")}
                        </div>
                        {item.diploma}
                      </div>
                    )}
                    {item.start && (
                      <div
                        className={
                          "_fp-studies-and-training-comparison__column__details__row"
                        }
                      >
                        <div
                          className={
                            "_fp-studies-and-training-comparison__column__details__row__label _fp-heading-5"
                          }
                        >
                          {t(
                            "CAREER-STUDIES_AND_TRAINING-OVERVIEW-TRAINING_START",
                          )}
                        </div>
                        {item.start}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {item.youtubeVideoId && (
                <div
                  className={
                    "_fp-studies-and-training-comparison__column__video"
                  }
                >
                  <InlineYoutubeVideo youtubeId={item.youtubeVideoId} />
                </div>
              )}
            </div>
          )
        })}
      </div>
      {children}
    </Tile>
  )
}

export default StudiesAndTrainingComparison

StudiesAndTrainingComparison.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      title1: PropTypes.string.isRequired,
      title2: PropTypes.string.isRequired,
      title3: PropTypes.string,
      title4: PropTypes.string.isRequired,
      thatAwaitsYou: PropTypes.string.isRequired,
      yourSkills: PropTypes.string.isRequired,
      university: PropTypes.string,
      duration: PropTypes.string,
      diploma: PropTypes.string,
      start: PropTypes.string,
      youtubeVideoId: PropTypes.string,
    }),
  ).isRequired,
}
